/**
 * Firestore Client Stub
 *
 * This module provides stub implementations when Firebase SDK is not installed.
 * All operations gracefully return empty data, allowing the app to run without
 * Firebase while using GCP Vertex AI for other operations.
 *
 * Note: If Firebase/Firestore is needed in the future, install the firebase package:
 *   npm install firebase
 * Then restore the original imports from firebase/app and firebase/firestore.
 *
 * Project: slam-orioles (when configured)
 * Collection: tell_feedback
 *
 * @module firestore
 */

// ============================================================================
// STUB IMPLEMENTATIONS (Firebase SDK not installed)
// ============================================================================

// Stub firebase/app functions
const initializeApp = (config) => ({ name: '[STUB]', options: config });
const getApps = () => [];

// Stub firebase/firestore functions
const getFirestore = () => null;

// ============================================================================
// CONFIGURATION
// ============================================================================

// GCP/Firestore configuration for slam-orioles project
// These are client-side safe credentials (API key is restricted by domain)
const firestoreConfig = {
  apiKey: process.env.REACT_APP_GCP_API_KEY,
  authDomain: 'slam-orioles.firebaseapp.com',
  projectId: process.env.REACT_APP_GCP_PROJECT_ID || 'slam-orioles',
  storageBucket: 'slam-orioles.appspot.com',
};

// Initialize app only once (handles hot reloading in development)
let app;
let db;

/**
 * Initialize Firestore connection (stub version)
 * @returns {{app: Object, db: Object}} Stub app and null db instances
 */
function initializeFirestore() {
  if (getApps().length === 0) {
    app = initializeApp(firestoreConfig);
  } else {
    app = getApps()[0];
  }
  db = getFirestore(app);
  return { app, db };
}

// Initialize on module load
const firestore = initializeFirestore();

export { firestore };
export const firestoreApp = firestore.app;
export const firestoreDb = firestore.db;
// Alias for backwards compatibility
export { firestoreDb as db };

/**
 * Check if Firestore is properly configured
 * Since Firebase SDK is not installed, this always returns false.
 * @returns {boolean} Always false (Firebase not installed)
 */
export function isFirestoreConfigured() {
  // Firebase SDK not installed - always return false
  // This causes feedbackService to use graceful fallbacks
  return false;
}
